/*
 * Copyright (c) 2023 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { checkStringValueAndTranslate, checkValue } from '../../utils/utils';
import {
    DELAYED_DOWNLOADINGS_TYPE_TO_FILE_NAME_PREFIX,
    DelayedDownloadingStatus,
    DelayedDownloadingsListItemDto,
} from '../../data/api/downloadings/downloadingsClient.typings';
import { ListTableColumnConfig } from '../RegistryWidgets/Common/ListTypes';
import { WithTranslation } from 'react-i18next';
import moment from 'moment';
import { theme } from '../../assets/style/them';

export interface DelayedDownloadingsListItemTableData {
    selected: boolean;
    errorMessage?: string;
    type: string;
    requestedDate: string;
    status: string;

    fileNameForUser: string;
    management: boolean;
}

export const generateDownloadedFileName = (item: DelayedDownloadingsListItemDto): string => {
    return (
        DELAYED_DOWNLOADINGS_TYPE_TO_FILE_NAME_PREFIX[item.type] +
        checkValue(moment.utc(item.requestedDateAtUtc).local().format('__YYYY_MM_DD__HH_mm_ss'))
    );
};

export const COLOR_BY_DELAYED_DOWNLOADINGS_ITEM_STATUS: Record<DelayedDownloadingStatus, string> = {
    ERROR: theme.palette.error.main,
    READY: theme.palette.primary.main,
    CREATED: theme.palette.text.primary,
    IN_PROGRESS: theme.palette.text.primary,
};

export const createTableData = (data: DelayedDownloadingsListItemDto[], t: WithTranslation['t']) => {
    return data.reduce((tableData, item) => {
        const dataItem: Partial<DelayedDownloadingsListItemTableData> = {};

        dataItem.type = checkStringValueAndTranslate(item.type, 'downloadings.downloadingTypes.', t);
        dataItem.requestedDate = checkValue(moment.utc(item.requestedDateAtUtc).local().format('DD.MM.YYYY HH:mm:ss'));
        dataItem.status = checkValue(item.status); //do not translate to determine status color first

        dataItem.fileNameForUser = generateDownloadedFileName(item);
        dataItem.management = true;

        tableData.push(dataItem);

        return tableData;
    }, []);
};

export const delayedDownloadingsListConfig: ListTableColumnConfig<DelayedDownloadingsListItemTableData>[] = [
    {
        keyForValue: 'selected',
        translatePath: null,
        displayCustomCell: true,
        displayCustomHeader: true,
    },
    {
        keyForValue: 'requestedDate',
        translatePath: 'downloadings.downloadingRequestedDate',
        align: 'left',
    },
    {
        keyForValue: 'type',
        translatePath: 'app.type',
        align: 'left',
    },
    {
        keyForValue: 'status',
        translatePath: 'app.status',
        displayCustomCell: true,
    },
    {
        keyForValue: 'fileNameForUser',
        translatePath: 'app.fileName',
        isWithTooltip: true,
        align: 'left',
        maxWidth: 300,
    },
    {
        keyForValue: 'management',
        translatePath: 'app.management',
        displayCustomCell: true,
    },
];

export const getNumberOfFilesInProgress = (data: DelayedDownloadingsListItemDto[]): number => {
    if (!data || data.length === 0) return 0;
    else
        return data.reduce((sum, file) => {
            if (file.status === DelayedDownloadingStatus.Created || file.status === DelayedDownloadingStatus.InProgress)
                sum += 1;
            return sum;
        }, 0);
};
